import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRxCollection, useRxData } from 'rxdb-hooks';
import { Subscription } from 'rxjs';

import { useMst } from '@/mobx-models/Root';

import { RxdbCollectionName } from '../rxdbCollectionName';
import RxdbManager from '../RxdbManager';
import { BorerStateType, BorerStateTypeCollection, BorerStateTypeDocument } from './queryBuilder';

export interface BorerStateTypeSummary extends BorerStateType {
  categoryName: string;
  id: string;
}
export const useBorerStateType = (
  includeInactiveStateTypes = false,
  includeItemsWithoutTimeUsageModel = false,
) => {
  // // Need to filter by equipmentTypeId
  const { equipment } = useMst();

  // Collection initialization handling
  const borerStateTypeCollection: BorerStateTypeCollection = useRxCollection(
    RxdbCollectionName.BORER_STATE_TYPE,
  );

  const [augmentedStateTypesWithCategories, setAugmentedStateTypesWithCategories] = useState<
    BorerStateTypeSummary[]
  >([]);

  const [runningStateTypes, setRunningStateTypes] = useState<BorerStateTypeSummary[]>([]);

  const borerStateTypeQuery = useCallback(
    collection => {
      return collection.find({
        selector: {
          equipmentTypeId: equipment.selectedBorerEquipmentTypeId,
        },
      });
    },
    [equipment.selectedBorerEquipmentTypeId],
  );

  const { result: borerStateTypes } = useRxData<BorerStateType>(
    RxdbCollectionName.BORER_STATE_TYPE,
    borerStateTypeQuery,
  );

  const [defaultNonRunningStateType, setDefaultNonRunningStateType] = useState<
    BorerStateTypeSummary | undefined
  >();

  useEffect(() => {
    const augmentStateTypesWithCategory = async () => {
      if (borerStateTypes.length) {
        const stateTypesWithCategory: BorerStateTypeSummary[] = await Promise.all(
          borerStateTypes.map(async stateType => {
            const category = await stateType.populate('borerStateTypeCategoryId');

            return {
              ...stateType,
              categoryName: category?.description || 'No Category',
              description: stateType.description,
              definition: stateType.definition,
              id: stateType.id,
              isRunning: stateType.isRunning,
              isActive: stateType.isActive,
              timeUsageTypeId: stateType.timeUsageTypeId,
              isPlanned: stateType.isPlanned,
              isDefault: stateType.isDefault,
            };
          }),
        );

        const defaultNonRunning = stateTypesWithCategory.find(
          stateType => stateType?.isDefault === true && stateType?.isRunning === false,
        );

        setRunningStateTypes(
          stateTypesWithCategory.filter(stateType => stateType?.isRunning === true),
        );

        setDefaultNonRunningStateType(defaultNonRunning);

        setAugmentedStateTypesWithCategories(
          stateTypesWithCategory
            .filter(i => i?.isActive || includeInactiveStateTypes)
            .filter(
              i => Boolean(i?.timeUsageTypeId) || includeItemsWithoutTimeUsageModel || i.isDefault,
            ),
        );
      } else {
        setAugmentedStateTypesWithCategories([]);
      }
    };
    augmentStateTypesWithCategory();
  }, [borerStateTypes, includeInactiveStateTypes, includeItemsWithoutTimeUsageModel]);

  const borerStateTypeInitialized = useMemo((): boolean => {
    return !!borerStateTypeCollection;
  }, [borerStateTypeCollection]);

  // Get all types
  const [allStateTypes, setAllStateTypes] = useState<BorerStateTypeDocument[]>();
  const [allStateTypesById, setAllStateTypesById] =
    useState<Record<string, BorerStateTypeDocument>>();

  const listAllDelayTypes = useCallback(() => {
    if (!borerStateTypeCollection) {
      return [];
    }
    return borerStateTypeCollection.find().exec();
  }, [borerStateTypeCollection]);

  useEffect(() => {
    let subscription: Subscription;
    if (borerStateTypeInitialized && borerStateTypeCollection) {
      const getData = async () => {
        const delays = await listAllDelayTypes();
        const byId = delays.reduce((acc, cur) => {
          acc[cur.id] = cur;
          return acc;
        }, {} as Record<string, BorerStateTypeDocument>);

        setAllStateTypes(delays);
        setAllStateTypesById(byId);
      };

      subscription = borerStateTypeCollection?.$.subscribe(() => getData());

      getData();
    }

    return () => {
      subscription?.unsubscribe();
    };
  }, [borerStateTypeInitialized, listAllDelayTypes, borerStateTypeCollection]);

  return {
    borerStateTypeInitialized,
    listAllDelayTypes,
    allStateTypes,
    allStateTypesById,
    augmentedStateTypesWithCategories,
    defaultNonRunningStateType,
    runningStateTypes,
  };
};

export const getStateTypeById = async (id: string) =>
  RxdbManager.instance.db?.collections[RxdbCollectionName.BORER_STATE_TYPE]
    .findOne({
      selector: {
        id,
      },
    })
    .exec();

export default useBorerStateType;
